import React, { useState, useEffect } from 'react';
import { FiX, FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiCheckCircle, FiArrowRight, FiArrowLeft, FiUserCheck } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './AuthModal.css';

/**
 * Modern Dual-Pane Animated Auth Modal with Stepper for Registration.
 */
const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login' or 'signup'
  const [signupStep, setSignupStep] = useState(1); // 1: Profile, 2: Security
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'customer'
  });

  // Sync mode with initialMode when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setSignupStep(1);
      setError('');
      setSuccess('');
      setLoggedInUser(null);
      setFormData({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'customer'
      });
    }
  }, [isOpen, initialMode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (signupStep === 1) {
      if (!formData.fullName || !formData.email) {
        setError('Please fill in profile details.');
        return;
      }
      setSignupStep(2);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'signup' && signupStep === 1) {
      handleNextStep(e);
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const user = await userService.login(formData.email, formData.password);
        setLoggedInUser(user);
        authLogin(user);
        setSuccess('Logged in successfully!');
        
        setTimeout(() => {
          if (user.role === 'admin') navigate('/admin/dashboard');
          onClose();
        }, 800);
      } else {
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match.');
        }

        const user = await userService.register(formData.fullName, formData.email, formData.password, formData.role);
        setLoggedInUser(user);
        authLogin(user);
        setSuccess('Account created! Logging you in...');

        setTimeout(() => {
          if (user.role === 'admin') navigate('/admin/dashboard');
          onClose();
        }, 800);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`auth-modal-overlay ${isOpen ? 'active' : ''}`} onClick={(e) => e.target.classList.contains('auth-modal-overlay') && onClose()}>
      <div className={`auth-modal-container ${mode === 'signup' ? 'signup-mode' : 'login-mode'}`}>
        <button className="auth-close-btn" onClick={onClose}><FiX /></button>

        <div className="auth-content-main">
          {success ? (
            <div className="auth-success-minimal">
              <div className="minimal-success-icon"><FiCheckCircle /></div>
              <h2 className="minimal-success-title">Authenticated</h2>
              <p className="minimal-success-note">Accessing administration panel...</p>
            </div>
          ) : (
            <div className="auth-form-container-v5">
              <div className="auth-form-pane-v5">
                <div className="form-header-v5">
                  <h2 className="form-title-v5">Admin Portal</h2>
                  <p className="form-subtitle-v5">Enter your credentials to continue</p>
                </div>

                {mode === 'signup' && (
                   <div className="auth-stepper-v5">
                      <div className={`stepper-dot ${signupStep >= 1 ? 'active' : ''}`}>1</div>
                      <div className={`stepper-line ${signupStep === 2 ? 'active' : ''}`}></div>
                      <div className={`stepper-dot ${signupStep === 2 ? 'active' : ''}`}>2</div>
                   </div>
                )}

                {error && <div className="auth-error-v5">{error}</div>}

                <form className="auth-form-v5" onSubmit={handleSubmit} autoComplete="off">
                  {mode === 'signup' && signupStep === 1 && (
                    <div className="step-content">
                      <div className="auth-input-v5">
                        <label>Full Name</label>
                        <div className="input-field-wrap">
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="enter your name"
                            required
                          />
                          <FiUser className="field-icon" />
                        </div>
                      </div>
                      <div className="auth-input-v5">
                        <label>Email</label>
                        <div className="input-field-wrap">
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="enter your email"
                            required
                          />
                          <FiMail className="field-icon" />
                        </div>
                      </div>
                      <div className="auth-input-v5">
                        <label>Account Role</label>
                        <div className="input-field-wrap">
                          <select
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            style={{ 
                              width: '100%', 
                              padding: '12px', 
                              paddingRight: '40px', 
                              background: 'transparent', 
                              border: 'none', 
                              color: 'rgba(255, 255, 255, 0.9)', 
                              outline: 'none', 
                              appearance: 'none', 
                              cursor: 'pointer', 
                              fontSize: '0.9rem', 
                              fontWeight: '500' 
                            }}
                          >
                            <option value="customer" style={{ background: '#0f172a', color: 'white' }}>Customer</option>
                            <option value="admin" style={{ background: '#0f172a', color: 'white' }}>Administrator</option>
                          </select>
                          <FiUserCheck className="field-icon" />
                        </div>
                      </div>
                    </div>
                  )}

                  {mode === 'signup' && signupStep === 2 && (
                    <div className="step-content">
                      <div className="auth-input-v5">
                        <label>Password</label>
                        <div className="input-field-wrap">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="enter your password"
                            required
                          />
                          <FiLock className="field-icon" />
                          <button
                            type="button"
                            className="v5-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </button>
                        </div>
                      </div>
                      <div className="auth-input-v5">
                        <label>Confirm Password</label>
                        <div className="input-field-wrap">
                          <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="enter your password"
                            required
                          />
                          <FiLock className="field-icon" />
                        </div>
                      </div>
                    </div>
                  )}

                   {mode === 'login' && (
                    <>
                      <div className="auth-input-v5">
                        <div className="input-field-wrap">
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            required
                            autoFocus
                            autoComplete="new-email"
                          />
                          <FiMail className="field-icon" />
                        </div>
                      </div>

                      <div className="auth-input-v5">
                        <div className="input-field-wrap">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter your password"
                            required
                            autoComplete="new-password"
                          />
                          <FiLock className="field-icon" />
                          <button
                            type="button"
                            className="v5-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </button>
                        </div>
                      </div>

                      <div className="v5-forgot-wrap">
                        <span className="v5-forgot-link" onClick={() => { onClose(); navigate('/forgot-password'); }}>
                          Forgot password?
                        </span>
                      </div>
                    </>
                  )}

                  <div className="form-actions">
                    <div className="action-buttons-wrap">
                        {mode === 'signup' && signupStep === 2 && (
                          <button type="button" className="v5-back-btn" onClick={() => setSignupStep(1)}>
                            <FiArrowLeft />
                          </button>
                        )}
                        <button
                          type="submit"
                          className={`v5-submit-btn ${loading ? 'loading' : ''} ${mode === 'signup' && signupStep === 2 ? 'flex-3' : ''}`}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="spinner-v5"></div>
                          ) : (
                            mode === 'login' ? 'LOGIN' : (signupStep === 1 ? 'Next' : 'Create Account')
                          )}
                        </button>
                    </div>

                    {mode === 'signup' && (
                      <div className="form-footer-toggle">
                        <p>Already a member? <span onClick={() => setMode('login')}>SIGN IN</span></p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;

